import React, { useEffect, useState, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, VStack, HStack, Text, Box, Input, FormControl, FormLabel,
  SimpleGrid, useToast, Badge, Table, Thead, Tbody, Tr, Th, Td,
  IconButton, Tooltip, Divider,
} from '@chakra-ui/react';
import { FiUserPlus, FiTrash2, FiDownload } from 'react-icons/fi';
import axiosInstance from '../../../api/AxiosInstance';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  taller: any;
  onGuardado: () => void;
}

const ModalInscripciones: React.FC<Props> = ({ isOpen, onClose, taller, onGuardado }) => {
  const toast = useToast();
  const [inscripciones, setInscripciones] = useState<any[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState({
    nombre: '', apellidos: '', localidad: '', municipio: '', telefono: '', correo: '',
  });

  const cargar = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`/cultura/talleres/${taller.id}/inscripciones`);
      setInscripciones(data);
    } catch {}
  }, [taller.id]);

  useEffect(() => { if (isOpen) cargar(); }, [isOpen, cargar]);

  const set = (f: string, v: string) => setForm((p) => ({ ...p, [f]: v }));

  const handleRegistrar = async () => {
    if (!form.nombre || !form.apellidos) {
      toast({ title: 'Nombre y apellidos son requeridos', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      await axiosInstance.post(`/cultura/talleres/${taller.id}/inscripciones`, form);
      toast({ title: 'Inscripción registrada', status: 'success', duration: 2000 });
      setForm({ nombre: '', apellidos: '', localidad: '', municipio: '', telefono: '', correo: '' });
      cargar();
      onGuardado();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.response?.data?.message || 'Error al inscribir', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (id: number) => {
    try {
      await axiosInstance.delete(`/cultura/talleres/inscripcion/${id}`);
      toast({ title: 'Inscripción eliminada', status: 'success', duration: 2000 });
      cargar();
      onGuardado();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 });
    }
  };

  const exportarExcel = () => {
    const rows = inscripciones.map((i, idx) => ({
      '#': idx + 1,
      'Nombre': i.nombre,
      'Apellidos': i.apellidos,
      'Localidad': i.localidad || '',
      'Municipio': i.municipio || '',
      'Teléfono': i.telefono || '',
      'Correo': i.correo || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inscripciones');
    XLSX.writeFile(wb, `Inscripciones_${taller.titulo}.xlsx`);
  };

  const exportarPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(`Listado de Inscripciones: ${taller.titulo}`, 14, 16);
    autoTable(doc, {
      startY: 24,
      head: [['#', 'Nombre', 'Apellidos', 'Localidad', 'Municipio', 'Teléfono', 'Correo']],
      body: inscripciones.map((i, idx) => [
        idx + 1, i.nombre, i.apellidos, i.localidad || '', i.municipio || '', i.telefono || '', i.correo || '',
      ]),
      styles: { fontSize: 8 },
    });
    doc.save(`Inscripciones_${taller.titulo}.pdf`);
  };

  const cupoRestante = taller.cupo - inscripciones.length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
          Inscripciones — {taller.titulo}
        </ModalHeader>
        <ModalBody py={4}>
          <VStack spacing={4} align="stretch">
            <HStack spacing={3}>
              <Badge colorScheme={cupoRestante <= 0 ? 'red' : 'green'} px={3} py={1} borderRadius="full">
                Inscritos: {inscripciones.length}/{taller.cupo}
              </Badge>
              <Badge colorScheme={cupoRestante <= 0 ? 'red' : 'blue'} px={3} py={1} borderRadius="full">
                Disponibles: {Math.max(0, cupoRestante)}
              </Badge>
            </HStack>

            {cupoRestante > 0 && (
              <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                <Text fontSize="sm" fontWeight="bold" color="#5b182b" mb={3}>Registrar nuevo participante</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Nombre</FormLabel>
                    <Input size="sm" value={form.nombre} onChange={(e) => set('nombre', e.target.value)} />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Apellidos</FormLabel>
                    <Input size="sm" value={form.apellidos} onChange={(e) => set('apellidos', e.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Localidad</FormLabel>
                    <Input size="sm" value={form.localidad} onChange={(e) => set('localidad', e.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Municipio</FormLabel>
                    <Input size="sm" value={form.municipio} onChange={(e) => set('municipio', e.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Teléfono</FormLabel>
                    <Input size="sm" value={form.telefono} onChange={(e) => set('telefono', e.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Correo</FormLabel>
                    <Input size="sm" type="email" value={form.correo} onChange={(e) => set('correo', e.target.value)}
                      placeholder="Se enviará confirmación automática" />
                  </FormControl>
                </SimpleGrid>
                <Button mt={3} size="sm" leftIcon={<FiUserPlus />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }}
                  onClick={handleRegistrar} isLoading={guardando} loadingText="Registrando...">
                  Registrar inscripción
                </Button>
              </Box>
            )}

            <Divider />

            <HStack justify="space-between">
              <Text fontSize="sm" fontWeight="bold">Participantes ({inscripciones.length})</Text>
              {inscripciones.length > 0 && (
                <HStack>
                  <Button size="xs" leftIcon={<FiDownload />} variant="outline" onClick={exportarPDF}>PDF</Button>
                  <Button size="xs" leftIcon={<FiDownload />} colorScheme="green" variant="outline" onClick={exportarExcel}>Excel</Button>
                </HStack>
              )}
            </HStack>

            {inscripciones.length === 0 ? (
              <Text color="gray.400" fontSize="sm" textAlign="center">Sin inscripciones aún</Text>
            ) : (
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>#</Th><Th>Nombre</Th><Th>Apellidos</Th>
                      <Th>Localidad</Th><Th>Teléfono</Th><Th>Correo</Th><Th></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {inscripciones.map((i, idx) => (
                      <Tr key={i.id}>
                        <Td>{idx + 1}</Td>
                        <Td>{i.nombre}</Td>
                        <Td>{i.apellidos}</Td>
                        <Td>{i.localidad || '-'}</Td>
                        <Td>{i.telefono || '-'}</Td>
                        <Td fontSize="xs">{i.correo || '-'}</Td>
                        <Td>
                          <Tooltip label="Eliminar">
                            <IconButton aria-label="Eliminar" icon={<FiTrash2 />} size="xs"
                              colorScheme="red" variant="ghost" onClick={() => handleEliminar(i.id)} />
                          </Tooltip>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalInscripciones;
